import { Footer } from "~/components/Footer";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { NextSeo } from "next-seo";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { Routes } from "~/routes";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    FilterOperators,
    QueryResponse,
    SortDirections,
    Filter,
    FilterMetadata,
} from "~/modules/filters/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { LeagueTableGroupTypes } from "~/modules/league_tables/types";
import {
    MEASURE_IDS,
    SOCIAL_OG_IMAGE_URL,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import { AssetClassIndividualPage } from "~/modules/asset_classes/pages/individual";
import {
    TokenTimeseriesGroupBy,
    TokenTimeseriesColumns,
} from "~/modules/token_timeseries/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import {
    ASSET_CLASS_DESCRIPTIONS,
    ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS,
    GOVERNMENT_BOND_ASSET_QUERY,
    ASSET_CLASS_PAGES_TO_FILTER,
} from "~/modules/asset_classes/consts";
import { queryAssetFilterMeta, queryAssets } from "~/modules/assets/api/router";
import {
    queryTokenAggregateValueWith30D,
    queryTokenAggregateValueWith7D,
} from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import { AggregateValueTypes } from "~/components/AggregateDisplay/types";
import { getTimeseriesModes } from "~/modules/visualization/components/Timeseries/getTimeseriesModes";
import { ColumnMetadata } from "~/components/DataGrid";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { NET_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/NetAssetValue";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/BridgedTokenValueDollar";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { MANAGEMENT_FEE_COLUMN } from "~/modules/assets/components/AssetTable/columns/ManagementFee";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { MANAGER_COLUMN } from "~/modules/assets/components/AssetTable/columns/Manager";
import { APY_7_DAY_COLUMN } from "~/modules/assets/components/AssetTable/columns/Apy7Day";
import {
    PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN,
    PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
    PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN,
    PRIMARY_MARKET_REDEMPTION_FEE_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/PrimaryMarket";
import { TOTAL_PERFORMANCE_FEE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TotalPerformanceFee";
import { INCORPORATION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/IncorporationCountry";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import {
    DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
    DISTRIBUTION_TYPE_TOOLTIPS,
} from "~/consts";
import { LeagueTableTabsByDistributionType } from "~/modules/league_tables/types";
import { TOKENIZATION_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationType";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

const SEO_TITLE = "Tokenized Non-U.S. Government Debt";
const SEO_DESCRIPTION = ASSET_CLASS_DESCRIPTIONS.government_bonds;

// // // //

const GOVERNMENT_BONDS_TABLE_COLUMNS: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
>[] = [
    ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE,
    MANAGER_COLUMN,
    BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
    NET_ASSET_VALUE_COLUMN,
    APY_7_DAY_COLUMN,
    HOLDING_ADDRESS_COUNT_COLUMN,
    TOKENIZATION_TYPE_COLUMN,
    PLATFORM_COLUMN,
    NETWORKS_COLUMN,
    PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN,
    // WEIGHTED_AVG_MATURITY_COLUMN,
    INCORPORATION_COUNTRY_COLUMN,
    LICENSE_OR_REGULATORY_FRAMEWORK,
    PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
    MANAGEMENT_FEE_COLUMN,
    PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN,
    PRIMARY_MARKET_REDEMPTION_FEE_COLUMN,
    TOTAL_PERFORMANCE_FEE_COLUMN,
    CONTRACT_ADDRESS_COLUMN,
];

// // // //

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
// NOTE - this will have to be changed to hit api.rwa.xyz when the API has been updated
export async function getStaticProps() {
    // Base filters for all government bond queries
    const baseFilters: Filter[] =
        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.government_bonds;

    // Make all requests in parallel + destructure responses
    const [
        listQueryResponse,
        filterMetadata,
        leagueTableTabs,
        // netFlows,
        distributedAggregates,
        representedAggregates,
        holdersData,
    ] = await Promise.all([
        queryAssets({
            query: GOVERNMENT_BOND_ASSET_QUERY,
        }),
        queryAssetFilterMeta({
            filter: ASSET_CLASS_PAGES_TO_FILTER.government_bonds,
        }),
        queryLeagueTablesByDistributionType({
            name: "asset-class--government-bonds",
            filter: {
                operator: FilterOperators.And,
                filters: baseFilters,
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.managers,
                LeagueTableGroupTypes.networks,
                LeagueTableGroupTypes.jurisdictionCountries,
            ],
        }),
        // queryNetFlows({
        //     filters: [
        //         {
        //             field: TokenTimeseriesColumns.assetClassID,
        //             operator: FilterOperators.Equals,
        //             value: ASSET_CLASS_IDS.NonUSGovernmentDebt,
        //         },
        //     ],
        //     groupBy: TokenTimeseriesGroupBy.asset,
        //     limit: 5,
        // }),
        // 1. Distributed Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--government-bonds--distributed-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
            ],
        }),
        // 2. Represented Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--government-bonds--represented-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
            ],
        }),
        queryTokenAggregateValueWith7D({
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: baseFilters,
        }),
    ]);

    const aggregates: Array<AggregateDisplayProps> = [
        {
            label: "Distributed Value",
            value: distributedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: distributedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Represented Value",
            value: representedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: representedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Assets",
            value: listQueryResponse.results.length,
            type: AggregateValueTypes.count,
        },
        {
            label: "Holders",
            value: holdersData.value,
            type: AggregateValueTypes.count,
            tooltip:
                "The number of addresses that hold tokenized non-U.S. government debt.",
            percentChange: {
                value: holdersData.change7D,
                interval: "7d",
            },
        },
    ];

    // Trim down FilterMetadata options to only include the ones we need
    const trimmedFilterMetadata = filterMetadata.filter((each) => {
        return [AssetColumns.networkIDs, AssetColumns.protocolIDs].includes(
            each.field as AssetColumns,
        );
    });

    return {
        props: {
            listQueryResponse,
            filterMetadata: trimmedFilterMetadata,
            leagueTableTabs,
            aggregates,
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function GovernmentBondsPage(props: {
    aggregates: Array<AggregateDisplayProps>;
    listQueryResponse: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
    leagueTableTabs: LeagueTableTabsByDistributionType;
}) {
    const { aggregates, listQueryResponse, filterMetadata, leagueTableTabs } =
        props;

    const modes = getTimeseriesModes({
        analyticsKey: "government-bonds",
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        filters: ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.government_bonds,
    });

    return (
        <PageTrack route={Routes.governmentBonds}>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.governmentBonds)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.governmentBonds),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "tokenized government bonds, sovereign debt, non-US government debt, tokenized bonds, digital bonds, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.governmentBonds}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.governmentBonds,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => (
                    <Navbar route={Routes.governmentBonds} user={user} />
                )}
            </UserFetcher>
            <SidebarLayout pathname={Routes.governmentBonds}>
                <PageWrapper>
                    <AssetClassIndividualPage
                        listQueryResponse={listQueryResponse}
                        title={SEO_TITLE}
                        subtitle={SEO_DESCRIPTION}
                        assetsQuery={GOVERNMENT_BOND_ASSET_QUERY}
                        header={{
                            label: "Tokenized Non-U.S. Government Debt",
                            loading: false,
                            value: `${listQueryResponse.results.length.toLocaleString()} Total`,
                        }}
                        aggregates={aggregates}
                        timeseriesChart={{
                            title: "Tokenized Non-U.S. Government Debt Metrics",
                            modes,
                            distributionSelect: {
                                distributed: {
                                    enabled: true,
                                },
                                represented: {
                                    enabled: true,
                                },
                                stablecoins: {
                                    enabled: false,
                                    active: false,
                                },
                            },
                            columnSpanConfig: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            },
                        }}
                        pieChart={{
                            leagueTableData: leagueTableTabs,
                            title: "Tokenized Non-U.S. Govt. Debt Value",
                            defaultGroupBy: LeagueTableGroupTypes.platforms,
                            allowedGroupByValues: [
                                LeagueTableGroupTypes.platforms,
                                LeagueTableGroupTypes.issuers,
                                LeagueTableGroupTypes.networks,
                                LeagueTableGroupTypes.jurisdictionCountries,
                            ],
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        leagueTable={{
                            leagueTableData: leagueTableTabs,
                            analyticsTableName: "government-bonds-league-table",
                            title: "Tokenized Non-U.S. Govt. Debt League Table",
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        table={{
                            filterMetadata,
                            columns: GOVERNMENT_BONDS_TABLE_COLUMNS,
                            analyticsTableName: "government-bonds",
                            exportFilename: "government-bonds-assets",
                            sortQuery: {
                                field: AssetColumns.bridgedTokenValueDollar,
                                direction: SortDirections.desc,
                            },
                        }}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
